import React, { useState } from "react";
import { Avatar, Box, Card, CardContent, Chip, Divider, Grid2, IconButton, List, ListItem, ListItemAvatar, ListItemText, ListSubheader, Tooltip, Typography, useTheme } from "@mui/material";
import { FaRegFileLines } from "react-icons/fa6";
import { useNavigate, useNavigationType, useParams } from "react-router-dom";
import moment from "moment";
import { API_END_POINT } from "config";
import { IoMdArrowRoundBack } from "react-icons/io";
import HistoryTimeLine from "components/HistoryTimeLine";
import { FaRegEdit } from "react-icons/fa";
import { dispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import ClaimsComment from "./ClaimsComment";
import { useGetClaimsQuery } from "store/slices/apis/keyClaimsSlice";
import EditClaims from "./EditClaims";
import { usePermission } from "hooks/usePermission";
import { CLAIM_EDIT } from "config";
import { CLAIM_APPROVAL } from "config";
import { ReactComponent as LeftArrowicon } from "assets/PwC_Funct_Icons_ArrowLeft_Outline_Black_RGB.svg"

const grids = [
    {
        name: 'Workflow name',
        field: 'workflowApproval',
        renderer(params) {
            return (
                <Chip size="small" sx={{
                    fontSize: '12px',
                    '& .MuiChip-label': {
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        fontWeight: 600
                    }
                }} label={`${params?.workflowName ?? 'N/A'}`} />
            )
        }
    },
    {
        name: 'Category',
        field: 'package'
    },
    {
        name: 'Sub category 1',
        field: 'sub_package'
    },
    {
        name: "Contractor Number",
        field: "contract_number",
    },
    {
        name: "Consultant/Contractor",
        field: "consultant_contractor",
    },
    {
        name: "Claim Raised To",
        field: "claim_raised_to",
    },
    {
        name: "Claim Received On",
        field: "claim_received_on",
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format("DD/MM/YY")
                        : "N.A."}
                </>
            );
        },
    },
    {
        name: "Claim Raised By Reference",
        field: "claim_raised_by_refrence",
    },
    {
        name: "Claim Raised By Date Received",
        field: "claim_raised_by_date_received",
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format("DD/MM/YY")
                        : "N.A."}
                </>
            );
        },
    },
    {
        name: "Reason For Claim",
        field: "reason_for_claim",
    },
    {
        name: "Claim Raised To Date Of Response",
        field: "claim_raised_to_date_of_response",
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format(
                            "DD/MM/YY"
                        )
                        : "N.A."}
                </>
            );
        },
    },
    {
        name: "Claim Raised To Position",
        field: "claim_raised_to_position",
    },
    {
        name: "Approved Cost Impact",
        field: "approved_cost_impact",
    },
    {
        name: "Claim Raised By Estimate Cost Impact",
        field: "claim_raised_by_estimate_cost_impact",
    },
    {
        name: "Claim Raised To Estimate Cost Impact",
        field: "claim_raised_to_estimate_cost_impact",
    },
    {
        name: "Claim Raised By Estimate Time Impact",
        field: "claim_raised_by_estimate_time_impact",
    },
    {
        name: "Claim Raised To Estimate Time Impact",
        field: "claim_raised_to_estimate_time_impact",
    },
    {
        name: "Approved Time Impact",
        field: "approved_time_impact",
    },
    {
        name: "Actual Date Of Closure",
        field: "actual_date_of_closure",
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format("DD/MM/YY")
                        : "N.A."}
                </>
            );
        },
    },
    {
        name: "Status",
        field: "status",
        renderer(params) {
            return (
                <Chip size="small" sx={{
                    fontSize: '12px',
                    '& .MuiChip-label': {
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        fontWeight: 600
                    }
                }} label={`${params}`} />
            )
        }
    },
    {
        name: "Created Date",
        field: "createddate",
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format("DD/MM/YY")
                        : "N.A."}
                </>
            );
        },
    },

]


const ClaimsDetail = () => {

    const theme = useTheme();
    const navigate = useNavigate();
    const navigationType = useNavigationType();

    const { id, assignee, isEditable } = useParams();

    const decrypt = (encoded) => decodeURIComponent(escape(atob(encoded)));
    const decrypted = decrypt(assignee);

    const riskDetailRes = useGetClaimsQuery(id);
    const canEdit = usePermission(CLAIM_EDIT);
    const canApprove = usePermission(CLAIM_APPROVAL);

    const [isEditKeyRiskModal, setEditKeyRiskModal] = useState(false);
    const [workflowStatus, setWorkflowStatus] = useState('N/A');
    const [rowData, setRowData] = useState(false);

    const handleActionEdit = (row) => {
        setEditKeyRiskModal((prev) => {
            const newState = !prev;
            setRowData(newState ? row : null);
            return newState;
        });
    }

    const openNotification = async (msg, type = 'error') => {
        await dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), 3000);
    };

    return (
        <>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontSize: '24px', fontWeight: '500', color: '#252525', mb: 2, lineHeight: "32px", letterSpacing: '-0.4px' }}>
                        <Tooltip
                            placement="bottom"
                            title="Back"
                            arrow
                            slotProps={{
                                tooltip: { sx: { fontSize: "12px" } },
                            }}
                        >
                            {window.history.length > 1 && navigationType !== "POP" &&
                                <IconButton
                                    aria-label="download"
                                    size="small"
                                    onClick={() => {
                                        navigate(-1)
                                    }}
                                >
                                    <LeftArrowicon style={{ height: '22px', width: '22px', color: "#252525", stroke: "#252525", strokeWidth: 4 }} />
                                </IconButton>
                            }
                        </Tooltip>

                        Claims Detail
                    </Typography>
                </Box>
            </Grid2>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Grid2 size={12}>
                    <Card sx={{ borderRadius: 1, boxShadow: 'none', p: 3, pt: 1.5 }}>
                        <Box sx={{ width: '100%', mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: '500',
                                    color: '#252525',
                                    lineHeight: '32px',
                                    letterSpacing: '-0.4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                {`Claim ID`}
                                <Chip
                                    label={`${riskDetailRes?.data?.data?.claim_number}`}
                                    size="small"
                                    sx={{
                                        height: '20px',
                                        color: '#fff',
                                        fontSize: '12px',
                                        backgroundColor: `${theme?.customSettings?.themeColor}`,
                                    }}
                                />
                            </Typography>
                            {/* {canEdit && riskDetailRes?.data?.data?.status != 'Close' && isEditable == 'true' &&
                                <Tooltip
                                    placement="top"
                                    title={"Edit"}
                                    arrow
                                    slotProps={{
                                        tooltip: { sx: { fontSize: "12px" } },
                                    }}
                                >
                                    <IconButton
                                        aria-label="Edit Issue"
                                        size="small"
                                        onClick={() => handleActionEdit(riskDetailRes?.data?.data)}
                                    >
                                        <FaRegEdit />
                                    </IconButton>
                                </Tooltip>
                            } */}

                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <CardContent sx={{ p: 0 }}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, md: 3, sm: 4 }}>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>Workflow status</Typography>
                                    {/* <Typography gutterBottom variant="body2">At RMC</Typography> */}
                                    <Chip size="small" sx={{
                                        fontSize: '12px',
                                        '& .MuiChip-label': {
                                            paddingLeft: '8px',
                                            paddingRight: '8px',
                                            fontWeight: 600
                                        }
                                    }} label={`${workflowStatus}`} />
                                </Grid2>

                                {grids?.map((val, index) => {
                                    return (
                                        <Grid2 size={{ xs: 12, md: 3, sm: 4 }}>
                                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>{val?.name}</Typography>
                                            {val?.renderer ?
                                                val.renderer(riskDetailRes?.data?.data[val?.field])
                                                :
                                                <Typography gutterBottom variant="body2">{riskDetailRes?.data?.data[val?.field]}</Typography>
                                            }
                                        </Grid2>
                                    )
                                })}


                                <Grid2 size={{ xs: 12, md: 12, lg: 12 }}>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>Description of change</Typography>
                                    <Typography gutterBottom variant="body2">{riskDetailRes?.data?.data?.description_of_claim}</Typography>
                                </Grid2>

                                <Grid2 size={{ xs: 12, md: 12, lg: 12 }}>
                                    {riskDetailRes?.data?.data?.files?.length > 0 && (
                                        <>
                                            <List
                                                subheader={<ListSubheader sx={{ p: 0 }}>View Docuemnts</ListSubheader>}
                                            >
                                                {riskDetailRes?.data?.data?.files?.map(
                                                    (file, index) => (
                                                        <ListItem
                                                            key={index}
                                                            sx={{ padding: 0 }}
                                                        >
                                                            <ListItemAvatar>
                                                                <Avatar sx={{ background: "none" }}>
                                                                    <FaRegFileLines
                                                                        fontSize="inherit"
                                                                        style={{ color: "#474747" }}
                                                                    />
                                                                </Avatar>
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                                primary={
                                                                    <span
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            color: "#474747",
                                                                            textDecoration: "underline",
                                                                        }}
                                                                        onClick={() => {
                                                                            window.open(
                                                                                `${API_END_POINT}issues/view/${file?.name}`,
                                                                                "_blank"
                                                                            );
                                                                        }}
                                                                    >
                                                                        {file?.name}
                                                                    </span>
                                                                }
                                                            />
                                                        </ListItem>
                                                    )
                                                )}
                                            </List>
                                        </>
                                    )}
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <HistoryTimeLine id={id} workflow={setWorkflowStatus} />
                </Grid2>
                {canApprove && decrypted == 'edit' &&
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Card sx={{ borderRadius: 1, boxShadow: 'none', p: 3, pt: 1.5, minHeight: '250px' }}>
                            <Box sx={{ width: '100%', mb: 1.5 }}>
                                <Typography variant="h5" sx={{ fontSize: '20px', fontWeight: '500', color: '#252525', lineHeight: "32px", letterSpacing: '-0.4px' }}>
                                    Add comment
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <CardContent sx={{ p: 0 }}>
                                <ClaimsComment id={id} />
                            </CardContent>
                        </Card>
                    </Grid2>
                }

            </Grid2>
            {isEditKeyRiskModal && (
                <EditClaims
                    cancelIssue={handleActionEdit}
                    open={isEditKeyRiskModal}
                    data={riskDetailRes?.data?.data}
                    openNotification={openNotification}
                // refetch={refetch}
                />
            )}

        </>
    );
}

export default ClaimsDetail;
